import * as React from "react";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (option: { id: string; label: string; valor: string }) => void;
  onCreateNew: (label: string) => void;
  options: Array<{ id: string; label: string; valor: string }>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  emptyText?: string;
}

export function AutocompleteInput({
  value,
  onChange,
  onSelect,
  onCreateNew,
  options,
  placeholder = "Digite para buscar ou criar...",
  className,
  disabled = false,
  emptyText = "Nenhum resultado. Pressione Enter para criar novo.",
}: AutocompleteInputProps) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = React.useState(value);

  // Sincronizar inputValue com value prop
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Filtrar opções baseado no texto digitado
  const filteredOptions = React.useMemo(() => {
    if (!inputValue.trim()) return options;
    const search = inputValue.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(search) ||
        opt.valor.toLowerCase().includes(search)
    );
  }, [inputValue, options]);

  // Verificar se o texto digitado corresponde exatamente a uma opção
  const exactMatch = React.useMemo(() => {
    if (!inputValue.trim()) return null;
    return options.find(
      (opt) =>
        opt.label.toLowerCase() === inputValue.toLowerCase() ||
        opt.valor.toLowerCase() === inputValue.toLowerCase()
    );
  }, [inputValue, options]);

  // Abrir popover quando há texto e opções
  React.useEffect(() => {
    if (inputValue.trim() && filteredOptions.length > 0 && !disabled) {
      setOpen(true);
    } else if (inputValue.trim() && filteredOptions.length === 0 && !disabled) {
      setOpen(true); // Mostrar mesmo sem resultados para indicar que pode criar
    } else {
      setOpen(false);
    }
  }, [inputValue, filteredOptions.length, disabled]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleSelect = (option: { id: string; label: string; valor: string }) => {
    onSelect(option);
    setInputValue("");
    onChange("");
    setOpen(false);
    // Focar novamente no input após um pequeno delay
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      
      // Se há correspondência exata, selecionar
      if (exactMatch) {
        handleSelect(exactMatch);
        return;
      }

      // Se não há correspondência, criar novo
      onCreateNew(inputValue.trim());
      setInputValue("");
      onChange("");
      setOpen(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleBlur = () => {
    // Delay para permitir clicar em uma opção antes de fechar
    setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (inputValue.trim()) setOpen(true);
        }}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
      />
      
      {open && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            side="bottom"
            sideOffset={4}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command shouldFilter={false}>
              <CommandList>
                {filteredOptions.length === 0 ? (
                  <CommandEmpty>
                    {emptyText}
                  </CommandEmpty>
                ) : (
                  <CommandGroup>
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.id}
                        value={option.label}
                        onSelect={() => handleSelect(option)}
                        className="cursor-pointer"
                      >
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

